import Export20 from '@carbon/icons-react/es/export/20';
import StarReview20 from '@carbon/icons-react/es/star--review/20';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { User } from 'api/models';
import { CsatAPI, CsatRating, Template } from 'api/reports/csat';
import { FieldOptionBase } from 'api/tasks';
import { ProjectInUserProfile } from 'api/user';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Button } from 'components/atoms/button';
import { TableSkeleton } from 'components/atoms/table';
import { ActionBar } from 'components/molecules/action-bar';
import { ActionableFilters } from 'components/molecules/filters/actionable-filter';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { Sticky as StickyMolecule } from 'components/molecules/sticky';
import { UsersGroupedByCompanyComponents } from 'components/organisms/filter-editors';
import {
  CustomRangePicker,
  FilterContainer,
} from 'components/organisms/projects-report/common';
import {
  parseDateRange,
  stringifyDateRange,
} from 'components/organisms/projects-report/common/range-picker/transformer';
import {
  DateRange,
  getRangeObject,
  getValueFromDateRange,
  Range,
} from 'components/organisms/projects-report/common/range-picker/types';
import { UserEditorComponents } from 'components/organisms/tasks/criteria/editor-components';
import {
  FieldType,
  SingleSelectField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { GetSelectEditorComponents } from 'components/organisms/tasks/filter-box/utils';
import { useFilters } from 'components/organisms/tasks/use-filters';
import { RangePickerTrigger } from 'components/pages/projects-report/range-picker-trigger';
import { theme } from 'components/styles/theme';
import { Argon } from 'components/templates';
import { useSearch } from 'hooks/use-search';
import {
  TranslationKey,
  useTranslate,
  useTranslation,
} from 'hooks/use-translation';
import {
  useAvgProjectManagerRatingsQuery,
  useAvgTemplateRatingsQuery,
  useGetCSATData,
  useOverAllRatingsQuery,
} from 'queries/reports/csat';
import { getRandomColor } from 'utils/get-random-color';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { FIELD_ID_RATINGS } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';
import { CSATEmptyState } from './empty-state';
import { GroupBy, GroupByMenu } from './group-by';
import { CsatList } from './list';
import {
  OverallRating,
  ProjectManagerRatings,
  Ratings,
  TemplateRatings,
  Widgets,
} from './widgets';

const Container = styled.div`
  padding: var(--spacing-05);
  padding-left: var(--left-spacing);
  background-color: var(--coolest-gray-10);
  overflow: auto;
  height: 100%;
`;

const Sticky = styled(StickyMolecule)`
  background: var(--coolest-gray-10);
`;

interface CsatOverviewProps {
  templates: Template[];
  projects: ProjectInUserProfile[];
  users: User[];
}

const RATINGS = Array.from({ length: 5 }, (_, i) => i + 1);

const fieldIdHash = {
  rating: FIELD_ID_RATINGS,
};

const rangeOptions = [
  Range.Anytime,
  Range.ThisYear,
  Range.ThisQuarter,
  Range.LastQuarter,
  Range.ThisMonth,
  Range.LastMonth,
  Range.Last90Days,
  Range.Advanced,
];
const getSelectEditorComponents: GetSelectEditorComponents = (field) => {
  if (field.fieldName === 'customer') {
    return UserEditorComponents;
  }
  if (field.fieldName === 'teamMembers') {
    return UsersGroupedByCompanyComponents;
  }

  return undefined;
};

export const CSATOverview: FC<CsatOverviewProps> = (props) => {
  const { projects, templates, users } = props;
  const translate = useTranslate();
  const projectFields = useProjectFieldsAsFilterableFields();

  const getFilterFields = useCallback(() => {
    const ratingFilterField: SingleSelectField = {
      fieldName: 'csatRating',
      fieldId: fieldIdHash.rating,
      metaData: {
        required: true,
        options: RATINGS.map((rating): FieldOptionBase => {
          const label = translate(`csat.rating.${rating}` as TranslationKey);

          return {
            value: rating,
            label,
            active: true,
            color: getRandomColor(label),
          };
        }).reverse(),
      },
      private: false,
      type: FieldType.SingleSelect,
      fieldLabel: translate('rating'),
      sourceType: SourceType.Project,
    };

    return [ratingFilterField];
  }, [translate]);

  const all_fields = useMemo(
    () =>
      projectFields.fields
        .filter(({ fieldName }) => fieldName !== 'partnerCompany')
        .concat(getFilterFields()),
    [getFilterFields, projectFields.fields],
  );

  const lookup = keyBy(all_fields, 'fieldName');

  const { selectFilter, filter, clearFilters } = useFilters(
    lookup,
    projectFields.loading ? 'loading' : 'loaded',
  );

  const [search, setSearch] = useSearch<{ range: DateRange }>({
    from: (value) => {
      const range = parseDateRange(
        (value.range ?? '') as string,
        getRangeObject(Range.Anytime),
      );

      return { range };
    },
    to: (value) => {
      return { range: stringifyDateRange(value.range) };
    },
  });
  const [_startDate, _endDate] = getValueFromDateRange(search.range);
  const [startDate, endDate] = [
    _startDate.format('YYYY-MM-DD'),
    _endDate.format('YYYY-MM-DD'),
  ];

  const [groupBy, setGroupBy] = useState(GroupBy.ResponseDate);

  const { data: overallRatings, isLoading: isOverallRatingsLoading } =
    useOverAllRatingsQuery(filter, startDate, endDate);

  const { data, isLoading } = useGetCSATData(
    filter,
    startDate,
    endDate,
    users,
    templates,
    projects,
  );

  const {
    data: projectManagersRatings,
    isLoading: isProjectManagersRatingsLoading,
  } = useAvgProjectManagerRatingsQuery(filter, startDate, endDate, users);

  const { data: templateRatings, isLoading: isTemplateRatingsLoading } =
    useAvgTemplateRatingsQuery(filter, startDate, endDate, templates);

  const exportLabel = useTranslation('export');

  const exportRequest = () =>
    CsatAPI.exportCsatData({ filter, endDate, startDate });
  const { isLoading: exporting, mutateAsync: exportData } = useMutation(
    exportRequest,
    {
      onSuccess: (result) => {
        window.open(result.downloadUrl, '_blank');
      },
    },
  );

  const [isEmpty, setIsEmpty] = useState(false);

  useEffect(() => {
    if (!isLoading && data && !data.length && !filter) {
      setIsEmpty(true);
    }
  }, [data, isLoading, filter]);

  return (
    <>
      <Helmet>
        <title>{useTranslation('reports.csat.title')}</title>
      </Helmet>
      <Argon
        breadcrumbs={
          <Breadcrumb.Item>
            <Link to="/reports">{useTranslation('reports')}</Link>
          </Breadcrumb.Item>
        }
        title={
          <HeaderWithIconTitle
            icon={<StarReview20 />}
            title={useTranslation('reports.csat.title')}
          />
        }
        actions={{
          primary: (
            <FilterContainer>
              <ActionableFilters
                fields={all_fields}
                filter={filter}
                loading={projectFields.loading}
                onChange={selectFilter}
                reset={clearFilters}
                getSelectEditorComponents={getSelectEditorComponents}
              />
              <CustomRangePicker
                range={search.range}
                onDateChange={(range) => {
                  setSearch({ range });
                }}
                triggerComponent={<RangePickerTrigger range={search.range} />}
                rangeOptions={rangeOptions}
              />
            </FilterContainer>
          ),
        }}
        content={
          isEmpty ? (
            <CSATEmptyState />
          ) : (
            <Container>
              <Widgets
                widget1={
                  <OverallRating
                    data={overallRatings}
                    isLoading={isOverallRatingsLoading}
                  />
                }
                widget2={
                  <Ratings
                    data={overallRatings}
                    isLoading={isOverallRatingsLoading}
                  />
                }
                widget3={
                  <ProjectManagerRatings
                    data={projectManagersRatings}
                    isLoading={isProjectManagersRatingsLoading}
                  />
                }
                widget4={
                  <TemplateRatings
                    data={templateRatings}
                    isLoading={isTemplateRatingsLoading}
                  />
                }
              />
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <>
                  <Sticky top={76} layer={theme.filters.layer - 1}>
                    <ActionBar aria-label="data table toolbar">
                      <ActionBar.Primary>
                        <GroupByMenu
                          disabled={!(data as Array<CsatRating>)?.length}
                          onChange={setGroupBy}
                        />
                      </ActionBar.Primary>
                      <ActionBar.Secondary>
                        <Button
                          iconPlacement="right"
                          icon={<Export20 />}
                          size="middle"
                          type="primary"
                          disabled={!(data as Array<CsatRating>)?.length}
                          loading={exporting}
                          onClick={() => exportData()}
                        >
                          {exportLabel}
                        </Button>
                      </ActionBar.Secondary>
                    </ActionBar>
                  </Sticky>
                  <CsatList
                    data={(data || []) as CsatRating[]}
                    isLoading={isLoading}
                    groupBy={groupBy}
                  />
                </>
              )}
            </Container>
          )
        }
      />
    </>
  );
};
